import { Injectable } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { ClientIPModalSettings, ClientIPOutsideSettings, ClientIPSettings } from '../../types/ClientIPListSettings'
import { map } from 'rxjs/operators'
import { DateUtils } from '../../../../../../services/date-utils.service'

@Injectable({
  providedIn: 'root',
})
export class ClientIpListSettingsService {
  readonly defaultModalSettings: ClientIPModalSettings = {
    showArchivedPrograms: false,
    showTerminatedProgramsEnrollments: false,
    showArchivedSubprograms: false,
    showTerminatedSubprogramsEnrollments: false,
    showClosedIP: false,
    showCompletedGoals: true,
    showCompletedSteps: true,
    dueDate: undefined,
  }

  readonly defaultOutsideSettings: ClientIPOutsideSettings = {
    search: '',
  }

  private modalSettingsSubject$$ = new BehaviorSubject<ClientIPModalSettings>(this.defaultModalSettings)
  private outsideSettingsSubject$$ = new BehaviorSubject<ClientIPOutsideSettings>(this.defaultOutsideSettings)

  combinedSettings$: Observable<ClientIPSettings> = combineLatest([
    this.modalSettingsSubject$$.asObservable(),
    this.outsideSettingsSubject$$.asObservable(),
  ]).pipe(
    map(([modalSettings, outsideSettings]) => ({
      ...modalSettings,
      ...outsideSettings,
    })),
  )

  resetModalSettings() {
    this.modalSettingsSubject$$.next(this.defaultModalSettings)
  }

  emptySettings() {
    const settings = this.modalSettingsSubject$$.value
    for (const key of Object.keys(this.defaultModalSettings) as (keyof ClientIPModalSettings)[]) {
      if (settings.hasOwnProperty(key)) {
        if (settings[key] instanceof Date) {
          const dateSetting = DateUtils.initializeDate(settings[key] as Date)
          const dateDefaultSettings = DateUtils.initializeDate(this.defaultModalSettings[key] as Date)
          if (
            (dateSetting && !dateDefaultSettings) ||
            (!dateSetting && dateDefaultSettings) ||
            (dateSetting && dateDefaultSettings && dateSetting.getTime() !== dateDefaultSettings.getTime())
          ) {
            return false
          }
        } else if (settings[key] !== this.defaultModalSettings[key]) {
          return false // Found difference
        }
      }
    }

    return true
  }

  getModalSettings() {
    return this.modalSettingsSubject$$.value
  }

  patchModalSettings(settings: Partial<ClientIPModalSettings>) {
    const { dueDate, ...rest } = settings
    this.modalSettingsSubject$$.next({
      ...this.getModalSettings(),
      ...rest,
      dueDate: DateUtils.initializeDate(dueDate),
    })
  }

  patchOutsideSettings(settings: Partial<ClientIPOutsideSettings>) {
    this.outsideSettingsSubject$$.next({ ...this.outsideSettingsSubject$$.value, ...settings })
  }

  getSettingsAsObservable() {
    return this.combinedSettings$
  }
}
